/**
 * Sanitizes credential update payloads before sending to the API.
 * The credential PUT endpoint expects primitive values for reference fields (e.g. industry_secondary,
 * compensation_currency_target). Sending nested objects (from GET responses) causes 400 Bad Request.
 *
 * Reference fields that must be primitive PKs: industry_secondary, seniority_user, candidate_type_user,
 * compensation_currency_target, compensation_currency_current.
 */
export function sanitizeCredentialPayload(payload: Record<string, any>): Record<string, any> {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const sanitized: Record<string, any> = {};
  const REFERENCE_FIELDS = [
    'industry_secondary',
    'seniority_user',
    'candidate_type_user',
    'compensation_currency_target',
    'compensation_currency_current',
  ] as const;

  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (REFERENCE_FIELDS.includes(key as any)) {
      const primitive = extractPrimitivePk(value);
      if (primitive !== undefined && primitive !== null) {
        sanitized[key] = primitive;
      }
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date)) {
      // Skip nested objects - API expects primitives for these fields
      continue;
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Extracts primitive PK from a value that may be an object (from API GET) or already a primitive.
 * Reference types may use `id` or `value` as the primary key.
 */
function extractPrimitivePk(value: any): number | string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object') {
    return value.id ?? value.value ?? value.code;
  }
  return undefined;
}
